import { Box, CardBrand, hasBankImage, Table, withStyles } from 'vopay';
import { useTranslation } from 'react-i18next';
import { EMBED_ASSETS_HOST } from '../constants';
import useConnectionTypeLabels from '../useConnectionTypeLabels';
import styles from './PaymentMethodCard.module.scss';

const cn = withStyles(styles);

export type PaymentMethodCardProps = {
  title: string;
  description: string;
  accountNumber: string;
  accountHolder: string;
  /** Bank accounts only — the API's raw 'manual' / 'online', localized here. */
  connectionType?: string;
  /** Credit cards only. */
  expiry?: string;
  creditCardBrand?: CardBrand;
  bankId?: string;
  type: 'credit' | 'bank';
};

// The "your default billing method is…" card. A Table in its `detail` variant, which lays the labels
// out as a row of headers with the values underneath — the layout this card has always had. `detail`
// exists only on Table1, so this is deliberately not moved to Table2 or DetailRows: both stack each
// label beside its value instead, which reads as a different card.
export default function PaymentMethodCard({
  title,
  description,
  accountNumber,
  accountHolder,
  connectionType,
  expiry,
  type,
  bankId,
  creditCardBrand,
}: PaymentMethodCardProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.PaymentMethodCard' });
  const { getConnectionTypeLabel } = useConnectionTypeLabels();

  return (
    <div className={cn('card')}>
      <h3 className={cn('title')}>{title}</h3>
      <p className={cn('description')}>{description}</p>

      <Box flx="col" jc="sb">
        <Table
          name="account-information"
          variant="detail"
          columns={{
            AccountNumber: { header: t('accountNumber', { defaultValue: 'Account Number' }), align: 'left' },
            AccountHolder: { header: t('accountHolder', { defaultValue: 'Account Holder' }), align: 'left' },
            ConnectionType: {
              header: t('connectionType', { defaultValue: 'Connection Type' }),
              align: 'left',
              visiblity: type === 'bank',
            },
            ExpDate: {
              header: t('expDate', { defaultValue: 'Exp. Date' }),
              align: 'left',
              visiblity: type === 'credit',
            },
          }}
          rows={[
            {
              AccountNumber: (
                <Box flx="row" ai="center">
                  <PaymentMethodLogo type={type} bankId={bankId} creditCardBrand={creditCardBrand} />
                  <span>{accountNumber}</span>
                </Box>
              ),
              AccountHolder: <Box pe="sm">{accountHolder || ''}</Box>,
              // Was capitalizeFirstLetter(ConnectionType): the same 'Manual' / 'Online' in English, and
              // localized rather than English under a French header.
              ConnectionType: getConnectionTypeLabel(connectionType),
              ExpDate: expiry || '',
            },
          ]}
        />
      </Box>
    </div>
  );
}

type PaymentMethodLogoProps = Pick<PaymentMethodCardProps, 'type' | 'bankId' | 'creditCardBrand'>;

function PaymentMethodLogo({ type, bankId, creditCardBrand }: PaymentMethodLogoProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.PaymentMethodCard' });

  if (type === 'bank') {
    if (!bankId || !hasBankImage(bankId)) return null;

    return (
      <img
        className={cn('logo')}
        alt={t('bankLogoAlt', { defaultValue: 'bank logo' })}
        src={`${EMBED_ASSETS_HOST}/images/logos-banks/${bankId}.svg`}
      />
    );
  }

  if (!creditCardBrand || creditCardBrand === 'unknown') return null;

  return (
    <img
      className={cn('logo')}
      alt={t('cardLogoAlt', { defaultValue: 'card logo' })}
      src={`${EMBED_ASSETS_HOST}/images/${creditCardBrand}.png`}
    />
  );
}
